import {
  AdditiveBlending,
  CanvasTexture,
  CylinderGeometry,
  DoubleSide,
  MathUtils,
  Mesh,
  Object3D,
  Quaternion,
  ShaderMaterial,
  Sprite,
  SpriteMaterial,
  Uniform,
  Vector3,
} from "three";

const vertexShader = /* glsl */ `
  uniform float time;
  varying vec2 vUv;
  varying float vRuffle;
  void main() {
    vUv = uv;
    float angle = uv.x * 6.2832;
    float h = uv.y;
    vec3 transformed = position;

    float wideEnd = 1.0 - h;
    float waveA = sin(angle * 5.0 + h * 12.0 - time * 5.8);
    float waveB = sin(angle * 9.0 - h * 17.0 + time * 3.6);
    float waveC = sin(angle * 2.0 + h * 8.0 + time * 7.4);
    vRuffle = waveA * 0.55 + waveB * 0.32 + waveC * 0.13;

    float radialWarp = 1.0 + vRuffle * 0.2 * wideEnd;
    transformed.xz *= radialWarp;

    vec2 sideways = vec2(cos(angle + 1.5708), sin(angle + 1.5708));
    transformed.xz += sideways * (waveB * 0.0055 * wideEnd * (1.0 - h * 0.55));

    gl_Position = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);
  }
`;

// UV конуса (CylinderGeometry): X = угол вокруг (0..1 = 0..2π), Y = высота (0=основание, 1=кончик)
const fragmentShader = /* glsl */ `
  uniform float time;
  uniform float progress;
  uniform float swirlStrength;
  varying vec2 vUv;
  varying float vRuffle;

  void main() {
    float angle = vUv.x * 6.2832;
    float h = vUv.y;

    float s1 = sin(angle * 3.0 - h * 12.0 - time * 7.2) * 0.5 + 0.5;
    float s2 = sin(angle * 6.0 - h * 8.5  - time * 5.3) * 0.5 + 0.5;
    float s3 = sin(angle * 2.0 + h * 15.0 + time * 3.8) * 0.5 + 0.5;
    float s4 = sin(angle * 11.0 + h * 22.0 - time * 9.4) * 0.5 + 0.5;

    float lines = s1 * 0.42 + s2 * 0.28 + s3 * 0.18 + s4 * 0.12;
    float narrowStreams = smoothstep(0.55, 0.98, lines);
    float softVeil = smoothstep(0.18, 0.74, lines);
    float tornEdge = smoothstep(-0.32, 0.42, vRuffle + (1.0 - h) * 0.55);
    float droplets = smoothstep(0.972, 1.0, s4 * s2 + s1 * 0.18) * (1.0 - h);
    float spiralA = sin(angle * 1.15 - h * 38.0 + time * 11.0);
    float spiralB = sin(angle * 1.15 - h * 38.0 + time * 11.0 + 3.1416);
    float spiralFade = smoothstep(0.05, 0.3, h) * (1.0 - smoothstep(0.86, 1.0, h));
    float spiralBands =
      (smoothstep(0.62, 0.96, spiralA) + smoothstep(0.68, 0.98, spiralB) * 0.7) *
      spiralFade *
      swirlStrength;

    float tipFade  = 1.0 - smoothstep(0.78, 1.0, h);
    float baseFade = smoothstep(0.0, 0.18, h);
    float brokenBaseFade = mix(0.64, 1.0, tornEdge);
    float fade = tipFade * baseFade * brokenBaseFade;

    // более сдержанные цвета под стиль подводной сцены
    vec3 deepTeal = vec3(0.0, 0.32, 0.44);
    vec3 aqua     = vec3(0.16, 0.72, 0.82);
    vec3 foam     = vec3(0.78, 1.0, 0.98);
    vec3 color = mix(deepTeal, aqua, softVeil);
    color = mix(color, foam, narrowStreams * 0.36 + droplets * 0.34);
    color = mix(color, foam, min(spiralBands * 0.62, 0.5));

    float visibility =
      progress < 0.12 ? progress / 0.12 :
      progress > 0.70 ? 1.0 - (progress - 0.70) / 0.30 :
      1.0;

    float pulse = 0.82 + sin(time * 14.0 - h * 16.0) * 0.18;
    float alpha = (softVeil * 0.14 + narrowStreams * 0.28 + droplets * 0.18 + spiralBands * 0.22) *
      fade * pulse * visibility;

    if (alpha < 0.01) discard;
    gl_FragColor = vec4(color, alpha);
  }
`;

const _up = new Vector3(0, 1, 0);
const _dir = new Vector3();
const _tipPos = new Vector3();
const _orientQ = new Quaternion();
const _spinQ = new Quaternion();

type SuctionVortexOptions = {
  forwardOffset?: number;
  renderOrder?: number;
  scale?: number;
  sustain?: boolean;
  swirlStrength?: number;
};

// getTipTransform заполняет позицию и направление оси носика (от предпоследней кости к кончику)
export function showSuctionVortex(
  scene: Object3D,
  getTipTransform: (position: Vector3, direction: Vector3) => void,
  duration: number,
  options: SuctionVortexOptions = {},
): () => void {
  const timeUniform = new Uniform(0);
  const progressUniform = new Uniform(0);
  const swirlStrengthUniform = new Uniform(options.swirlStrength ?? 0.7);
  const forwardOffset = options.forwardOffset ?? 0.07;
  const renderOrder = options.renderOrder ?? 1_000_003;
  const scaleMultiplier = options.scale ?? 1;
  const sustain = options.sustain ?? false;

  // конус: острие сверху (y = +height/2), основание снизу (y = -height/2)
  const geometry = new CylinderGeometry(0, 0.058, 0.095, 56, 12, true);
  const material = new ShaderMaterial({
    uniforms: {
      time: timeUniform,
      progress: progressUniform,
      swirlStrength: swirlStrengthUniform,
    },
    vertexShader,
    fragmentShader,
    transparent: true,
    depthWrite: false,
    side: DoubleSide,
    blending: AdditiveBlending,
  });

  const mesh = new Mesh(geometry, material);
  mesh.renderOrder = renderOrder;
  mesh.layers.set(1);
  // переворачиваем: острие (-Y после флипа) смотрит к носику, широкое основание — наружу
  mesh.rotation.x = Math.PI;

  const group = new Object3D();
  group.add(mesh);
  scene.add(group);

  const startedAt = performance.now();
  let frameId = 0;

  const tick = () => {
    const elapsed = performance.now() - startedAt;
    const progress = sustain
      ? Math.min(elapsed / 160, 0.35)
      : MathUtils.clamp(elapsed / duration, 0, 1);
    const t = elapsed * 0.001;

    getTipTransform(_tipPos, _dir);
    // смещаем конус немного вперёд по оси носика, чтобы он не прятался внутри насадки
    group.position.copy(_tipPos).addScaledVector(
      _dir.lengthSq() > 0.0001 ? _dir : _up,
      forwardOffset,
    );

    // ориентируем группу так, чтобы +Y (острие конуса) совпало с направлением носика
    if (_dir.lengthSq() > 0.0001) {
      _orientQ.setFromUnitVectors(_up, _dir.normalize());
    }
    // накручиваем вращение вокруг оси носика поверх ориентации
    _spinQ.setFromAxisAngle(_dir.lengthSq() > 0.0001 ? _dir : _up, t * Math.PI * 1.8);
    group.quaternion.multiplyQuaternions(_spinQ, _orientQ);

    const appearing = Math.min(progress / 0.18, 1.0);
    const shrinking = 1.0 - MathUtils.smoothstep(0.6, 1.0, progress);
    group.scale.setScalar(
      MathUtils.lerp(0.3, 1.0, appearing) * shrinking * scaleMultiplier,
    );

    timeUniform.value = t;
    progressUniform.value = progress;

    if (sustain || progress < 1) {
      frameId = requestAnimationFrame(tick);
      return;
    }
    cleanup();
  };

  frameId = requestAnimationFrame(tick);

  const cleanup = () => {
    cancelAnimationFrame(frameId);
    scene.remove(group);
    geometry.dispose();
    material.dispose();
  };

  return cleanup;
}

// Яркий шар-свечение, который движется по трубке во время pipe-фазы засасывания.
// Вызывай update() каждый кадр в pipe-фазе, dispose() в onComplete.
export function createPipeTravelGlow(scene: Object3D) {
  const canvas = document.createElement("canvas");
  canvas.width = 128;
  canvas.height = 128;
  const ctx = canvas.getContext("2d")!;
  const c = 64;
  const grad = ctx.createRadialGradient(c, c, 0, c, c, 58);
  grad.addColorStop(0,    "rgba(255,255,255,1)");
  grad.addColorStop(0.12, "rgba(210,248,255,1)");
  grad.addColorStop(0.38, "rgba(0,190,255,0.75)");
  grad.addColorStop(0.72, "rgba(0,130,255,0.25)");
  grad.addColorStop(1,    "rgba(0,80,255,0)");
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(c, c, 58, 0, Math.PI * 2);
  ctx.fill();

  const texture = new CanvasTexture(canvas);
  const material = new SpriteMaterial({
    map: texture,
    transparent: true,
    opacity: 0,
    depthTest: false,
    depthWrite: false,
    blending: AdditiveBlending,
  });
  const sprite = new Sprite(material);
  sprite.renderOrder = 1_000_005;
  sprite.scale.setScalar(0.11);
  sprite.visible = false;
  scene.add(sprite);

  return {
    update(worldPos: Vector3, pipeProgress: number) {
      sprite.visible = true;
      sprite.position.copy(worldPos);
      const t = performance.now() * 0.004;
      const pulse = 0.72 + Math.sin(t * 9.0) * 0.28;
      // к концу трубки немного тускнеем
      material.opacity = pulse * MathUtils.lerp(0.95, 0.45, pipeProgress);
      sprite.scale.setScalar(MathUtils.lerp(0.11, 0.07, pipeProgress) * (0.88 + Math.sin(t * 6.5) * 0.12));
    },
    hide() {
      sprite.visible = false;
    },
    dispose() {
      scene.remove(sprite);
      material.dispose();
      texture.dispose();
    },
  };
}

// Шлейф из капель за мусором пока он летит к носику (фаза 1).
// Вызывай update() с world-позицией мусора каждый кадр фазы 1, hide() в фазе 2, dispose() в onComplete.
export function createSuctionTrail(scene: Object3D) {
  const TRAIL_LENGTH = 10;

  const canvas = document.createElement("canvas");
  canvas.width = 32;
  canvas.height = 32;
  const ctx = canvas.getContext("2d")!;
  const gr = ctx.createRadialGradient(16, 16, 0, 16, 16, 13);
  gr.addColorStop(0,    "rgba(210,248,255,1)");
  gr.addColorStop(0.35, "rgba(0,185,255,0.7)");
  gr.addColorStop(1,    "rgba(0,120,255,0)");
  ctx.fillStyle = gr;
  ctx.beginPath();
  ctx.arc(16, 16, 13, 0, Math.PI * 2);
  ctx.fill();
  const texture = new CanvasTexture(canvas);

  const posBuf: Vector3[] = Array.from({ length: TRAIL_LENGTH }, () => new Vector3());
  let head = 0;
  let count = 0;

  type Node = { sprite: Sprite; mat: SpriteMaterial };
  const nodes: Node[] = Array.from({ length: TRAIL_LENGTH }, () => {
    const mat = new SpriteMaterial({
      map: texture,
      transparent: true,
      opacity: 0,
      depthTest: false,
      depthWrite: false,
      blending: AdditiveBlending,
    });
    const sprite = new Sprite(mat);
    sprite.renderOrder = 1_000_004;
    sprite.visible = false;
    scene.add(sprite);
    return { sprite, mat };
  });

  return {
    update(worldPos: Vector3) {
      posBuf[head].copy(worldPos);
      head = (head + 1) % TRAIL_LENGTH;
      count = Math.min(count + 1, TRAIL_LENGTH);

      for (let i = 0; i < count; i++) {
        const age = i / TRAIL_LENGTH;
        const bufIdx = (head - 1 - i + TRAIL_LENGTH) % TRAIL_LENGTH;
        const { sprite, mat } = nodes[i];
        sprite.visible = true;
        sprite.position.copy(posBuf[bufIdx]);
        mat.opacity = (1 - age) * 0.6;
        sprite.scale.setScalar(MathUtils.lerp(0.042, 0.01, age));
      }
      for (let i = count; i < TRAIL_LENGTH; i++) {
        nodes[i].sprite.visible = false;
      }
    },
    hide() {
      nodes.forEach((n) => { n.sprite.visible = false; });
      count = 0;
      head = 0;
    },
    dispose() {
      nodes.forEach(({ sprite, mat }) => {
        scene.remove(sprite);
        mat.dispose();
      });
      texture.dispose();
    },
  };
}
